import { Grid } from "@mui/material";
import { Navigate, Route, Routes } from "react-router-dom";
import MainFooterWrapper from "./components/footer/wrappers/MainFooterWrapper";
import NavBar from "./components/navbar/NavBar";
import PrivateRoute from "./components/wrappers/PrivateRoute";
import GlobalData from "./contexts/GlobalData";
import NotificationsProvider from "./contexts/NotificationsProvider";
import PageTag from "./contexts/PageTag";
import ToggleDrawer from "./contexts/ToggleDrawer";
import { WarehouseLabelBufferProvider } from "./contexts/WarehouseLabelBuffer";
import { WarehouseShoppingListBufferProvider } from "./contexts/WarehouseShoppingListBuffer";
import { WebSocketProvider } from "./contexts/WebSocketProvider";
import * as permissionTitles from "./enums/PermissionTitles";
import * as tags from "./enums/PrivilegeTags";
import { ADMIN, EMPLOYEE, MASTER } from "./enums/Roles";
import * as UrlPaths from "./enums/UrlPaths";
import AboutUsPage from "./features/aboutUs/AboutUsPage";
import ReservationsPage from "./features/admin/appointments/ReservationsPage";
import ClinicPage from "./features/admin/clinic/ClinicPage";
import ClinicBloodDonors from "./features/admin/clinic/components/blooddonors/ClinicBloodDonors";
import DocumentCategories from "./features/admin/clinic/components/categories/DocumentCategories";
import ChangeLogoPage from "./features/admin/clinic/components/changeLogo/ChangeLogoPage";
import ClinicsUsers from "./features/admin/clinic/components/employees/ClinicsUsers";
import InformationFieldsPage from "./features/admin/clinic/components/informationFields/InformationFieldsPage";
import ClinicOptions from "./features/admin/clinic/components/options/ClinicOptions";
import ClinicSpeciesPage from "./features/admin/clinic/components/species/ClinicSpeciesPage";
import ClinicWarehousePage from "./features/admin/clinic/components/warehouse/ClinicWarehousePage";
import WarehouseQueueFabs from "./features/admin/clinic/components/warehouse/components/WarehouseQueueFabs";
import WarehouseItemPage from "./features/admin/clinic/components/warehouse/WarehouseItemPage";
import WorkingHoursPage from "./features/admin/clinic/components/workingHours/WorkingHoursPage";
import ClinicView from "./features/admin/clinic/singleView/ClinicView";
import PendingAppointmentsPage from "./features/admin/pendingAppointments/PendingAppointmentsPage";
import SingleUserPermission from "./features/admin/permissions/components/SingleUserPermission";
import EmployeeEdit from "./features/admin/permissions/singleView/EmployeeEdit";
import TemplatePage from "./features/admin/templates/TemplatesPage";
import SingleUserView from "./features/admin/users/SingleUserView";
import UsersView from "./features/admin/users/UsersView";
import AppointmentsPage from "./features/appointments/AppointmentsPage";
import AppointmentView from "./features/appointments/singleView/AppointmentView";
import UserChangePasswordPage from "./features/auth/passwordChange/UserChangePasswordPage";
import FeedbackPage from "./features/feedback/FeedbackPage";
import HomePage from "./features/home/HomePage";
import InboxPage from "./features/inbox/InboxPage";
import ClinicsPage from "./features/master/clinics/ClinicsPage";
import ClinicListPage from "./features/master/clinicList/ClinicListPage";
import SpeciesPage from "./features/master/species/SpeciesPage";
import ClinicUsersMasterView from "./features/master/users/ClinicUsersMasterView";
import PetsPage from "./features/pets/PetsPage";
import LegacyPetRedirect from "./features/pets/LegacyPetRedirect";
import ProfilePage from "./features/profile/ProfilePage";
import AiAssistant from "./features/saps/pages/AiAssistant";
import BoxesList from "./features/saps/pages/BoxesList";
import SingleBoxView from "./features/saps/pages/single/SingleBoxView";
import SinglePetView from "./features/saps/pages/single/SinglePetView";
import SingleTherapyView from "./features/saps/pages/single/SingleTherapyView";
import TermsAndConditionsPage from "./features/termsAndConditions/TermsAndConditionsPage";
import Archive from "./features/saps/pages/Archive";
import SingleInpatientArchive from "./features/saps/pages/single/SingleInpatientArchive";
import ImportUsersView from "./features/admin/users/pages/ImportUsersView";

// Roles allowed to open a page, passed to PrivateRoute. Master and the stacionar account can open every page.
// Routes without roles are open to every logged in user (USER role pages: home, inbox, appointments, pets...)
const CLINIC_STAFF = [ADMIN, EMPLOYEE];
const MASTER_ONLY = [MASTER];

export default function DefaultContainer() {
  return (
    <>
      <Grid container>
        <PageTag>
          <GlobalData>
            <WarehouseLabelBufferProvider>
            <WarehouseShoppingListBufferProvider>
            <WarehouseQueueFabs />
            <NotificationsProvider>
              <WebSocketProvider>
                <ToggleDrawer>
                  <MainFooterWrapper>
                    <Grid item xs={1}>
                      <ToggleDrawer>
                        <NavBar />
                      </ToggleDrawer>
                    </Grid>
                    <Grid item xs={12} sm={13}>
                      <Routes>
                        <Route
                          path={UrlPaths.HOME}
                          element={
                            <PrivateRoute tag={tags.HOME_PAGE}>
                              <HomePage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.INBOX}
                          element={
                            <PrivateRoute tag={tags.INBOX}>
                              <InboxPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.PENDING_APPOINTEMENT_DETAILS_ID}
                          element={
                            <PrivateRoute
                              tag={tags.PENDING_APPOINTMENT_DETAILS_ID}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_PENDING]}
                            >
                              <ToggleDrawer>
                                <AppointmentView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.PENDING_APPOINTMENTS}
                          element={
                            <PrivateRoute
                              tag={tags.PENDING_APPOINTMENTS}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_PENDING]}
                            >
                              <ToggleDrawer>
                                <PendingAppointmentsPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path={UrlPaths.RESERVATION_ADMINISTRATION}
                          element={
                            <PrivateRoute
                              tag={tags.RESERVATION_ADMIN}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_APPOINTMENT]}
                            >
                              <ToggleDrawer>
                                <ReservationsPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={
                            UrlPaths.SINGLE_RESERVATION_ADMINISTRATION_DETAILS_ID
                          }
                          element={
                            <PrivateRoute
                              tag={tags.SINGLE_RESERVATION_ADMIN_ID}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_APPOINTMENT]}
                            >
                              <ToggleDrawer>
                                <AppointmentView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_CLINIC]}
                            >
                              <ToggleDrawer>
                                <ClinicView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_OPTIONS_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_CLINIC]}
                            >
                              <ToggleDrawer>
                                <ClinicOptions />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_WAREHOUSE}
                          element={
                            <PrivateRoute
                              tag={tags.WAREHOUSE}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_WAREHOUSE]}
                            >
                              <ToggleDrawer>
                                <ClinicWarehousePage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_WAREHOUSE_ID}
                          element={
                            <PrivateRoute
                              tag={tags.WAREHOUSE}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_WAREHOUSE]}
                            >
                              <ToggleDrawer>
                                <ClinicWarehousePage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_WAREHOUSE_ITEM_ID}
                          element={
                            <PrivateRoute
                              tag={tags.WAREHOUSE}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_WAREHOUSE]}
                            >
                              <ToggleDrawer>
                                <WarehouseItemPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.MASTER_SINGLE_CLINIC_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={MASTER_ONLY}
                            >
                              <ToggleDrawer>
                                <ClinicPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_EMPLOYEES_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_CLINIC]}
                            >
                              <ToggleDrawer>
                                <ClinicsUsers />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.FEEDBACK_SUBMIT}
                          element={
                            <PrivateRoute tag={UrlPaths.FEEDBACK_SUBMIT}>
                              <ToggleDrawer>
                                <FeedbackPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_WORKINGHOURS_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_CLINIC]}
                            >
                              <ToggleDrawer>
                                <WorkingHoursPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_CHANGELOGO_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_LOGO_UPLOAD]}
                            >
                              <ToggleDrawer>
                                <ChangeLogoPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_SPECIES_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_CLINIC]}
                            >
                              <ToggleDrawer>
                                <ClinicSpeciesPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_BLOOD_DONORS}
                          element={
                            <PrivateRoute tag={tags.BLOOD_DONORS}>
                              <ToggleDrawer>
                                <ClinicBloodDonors />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_BLOOD_DONORS_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_CLINIC]}
                            >
                              <ToggleDrawer>
                                <ClinicBloodDonors />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_DOCUMENTCATEGORIES_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_DOCUMENT_CATEGORIES]}
                            >
                              <ToggleDrawer>
                                <DocumentCategories />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_INFORMATIONFIELDS_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_CUSTOM_INFORMATION_FIELD]}
                            >
                              <ToggleDrawer>
                                <InformationFieldsPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_EMPLOYEE_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_CLINIC]}
                            >
                              <ToggleDrawer>
                                <EmployeeEdit />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_USER_PERMISSIONS_ID}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_CLINIC]}
                            >
                              <ToggleDrawer>
                                <SingleUserPermission />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path={UrlPaths.APPOINTEMENT_DETAILS_ID}
                          element={
                            <PrivateRoute tag={tags.APPOINTMENT_DETAILS_ID}>
                              <ToggleDrawer>
                                <AppointmentView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.APPOINTEMENTS}
                          element={
                            <PrivateRoute tag={tags.APPOINTMENTS}>
                              <AppointmentsPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.PETS}
                          element={
                            <PrivateRoute tag={tags.USER_PAGE}>
                              <PetsPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.CLINIC_USERS}
                          element={
                            <PrivateRoute
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_CLINIC]}
                            >
                              <ToggleDrawer>
                                <UsersView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_CLINIC_USER_ID}
                          element={
                            <PrivateRoute
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_USER_ADMINISTRATION]}
                            >
                              <ToggleDrawer>
                                <SingleUserView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SPECIES_ADMINISTRATION}
                          element={
                            <PrivateRoute
                              roles={MASTER_ONLY}
                            >
                              <SpeciesPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.CLINIC_LIST}
                          element={
                            <PrivateRoute
                              tag={tags.CLINIC_LIST}
                              roles={MASTER_ONLY}
                            >
                              <ClinicListPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.ADMINISTRATION}
                          element={
                            <PrivateRoute
                              tag={tags.ADMINISTRATION}
                              roles={MASTER_ONLY}
                            >
                              <ToggleDrawer>
                                <ClinicsPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.PROFILE_EDIT}
                          element={
                            <PrivateRoute tag={tags.PROFILE_EDIT_VIEW}>
                              <ToggleDrawer>
                                <ProfilePage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.PET_DETAILS_ID}
                          element={
                            <PrivateRoute tag={tags.SINGLE_PET_VIEW}>
                              <ToggleDrawer>
                                <SinglePetView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.LEGACY_PET_DETAILS_ID}
                          element={<LegacyPetRedirect />}
                        />
                        <Route
                          path={UrlPaths.TEMPLATE}
                          element={
                            <PrivateRoute
                              tag={tags.TEMPLATE_PAGE}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_TEMPLATES]}
                            >
                              <TemplatePage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.TERMS_INTERNAL}
                          element={
                            <PrivateRoute tag={tags.TERMS_AND_CONDITIONS}>
                              <ToggleDrawer>
                                <TermsAndConditionsPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.ABOUT_US}
                          element={
                            <PrivateRoute tag={tags.ABOUT_US}>
                              <ToggleDrawer>
                                <AboutUsPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.USER_CHANGE_PASSWORD}
                          element={
                            <PrivateRoute tag={tags.USER_CHANGE_PASSWORD}>
                              <ToggleDrawer>
                                <UserChangePasswordPage />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.MASTER_CLINIC_USERS}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={MASTER_ONLY}
                            >
                              <ToggleDrawer>
                                <ClinicUsersMasterView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SAPS_HOME_PAGE}
                          element={
                            <PrivateRoute
                              tag={tags.SPAS_HOME_PAGE}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_STATIONARY]}
                            >
                              <ToggleDrawer>
                                <BoxesList />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SAPS_ARCHIVE}
                          element={
                            <PrivateRoute
                              tag={null}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_STATIONARY]}
                            >
                              <ToggleDrawer>
                                <Archive />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_PET_VIEW}
                          element={
                            <PrivateRoute
                              tag={tags.SAPS_SINGLE_PET_VIEW}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_STATIONARY]}
                            >
                              <ToggleDrawer>
                                <SinglePetView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_BOX_VIEW}
                          element={
                            <PrivateRoute
                              tag={tags.SAPS_SINGLE_BOX_VIEW}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_STATIONARY]}
                            >
                              <ToggleDrawer>
                                <SingleBoxView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_ARCHIVE_VIEW}
                          element={
                            <PrivateRoute
                              tag={tags.SAPS_SINGLE_ARCHIVE_VIEW}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_STATIONARY]}
                            >
                              <ToggleDrawer>
                                <SingleInpatientArchive />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.AI_ASSISTANT}
                          element={
                            <PrivateRoute
                              tag={tags.SAPS_AI}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_STATIONARY]}
                            >
                              <ToggleDrawer>
                                <AiAssistant />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.SINGLE_THERAPY_VIEW_ID}
                          element={
                            <PrivateRoute
                              tag={tags.SINGLE_THERAPY_VIEW}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_STATIONARY]}
                            >
                              <ToggleDrawer>
                                <SingleTherapyView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path={UrlPaths.IMPORT_USERS_PAGE}
                          element={
                            <PrivateRoute
                              tag={tags.CLINIC_USERS}
                              roles={CLINIC_STAFF}
                              permissions={[permissionTitles.VIEW_CLINIC]}
                            >
                              <ToggleDrawer>
                                <ImportUsersView />
                              </ToggleDrawer>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="*"
                          element={
                            <PrivateRoute tag={tags.HOME_PAGE}>
                              <Navigate to={UrlPaths.HOME} />
                            </PrivateRoute>
                          }
                        />
                      </Routes>
                    </Grid>
                  </MainFooterWrapper>
                </ToggleDrawer>
              </WebSocketProvider>
            </NotificationsProvider>
            </WarehouseShoppingListBufferProvider>
            </WarehouseLabelBufferProvider>
          </GlobalData>
        </PageTag>
      </Grid>
    </>
  );
}
